import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  BarChart3,
  MessageCircle,
  ShoppingBag,
  Undo2,
  Percent,
  Tag,
  Lock,
  ArrowUpRight,
  Sparkles,
  Package,
  Filter,
  RotateCcw,
  TrendingUp,
  TrendingDown,
  Download,
  RotateCw,
  Clock,
  Wallet,
  Star,
  Printer,
  Timer,
  ShoppingCart,
  Repeat,
  Users,
  AlertTriangle,
  UserX,
  Crown,
  MapPin,
} from "lucide-react";
import { PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer } from "recharts";
import api from "@/lib/api";
import { formatMoney, CURRENCY_SYMBOLS } from "@/lib/currency";

const GEO_COLORS = ["#FF4A74", "#161616", "#8B7FD6", "#FFB870", "#5FCB9A", "#5FA8FF", "#E4C1F9", "#F2A65A", "#4DD0E1", "#B0BEC5"];

const CACHE_TTL_MS = 2 * 60 * 60 * 1000; // 2 hours

function cacheKey(businessId, range) {
  return `cdm_analytics_v2_${businessId || "me"}_${range}`;
}

function readCache(key) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.ts || Date.now() - parsed.ts > CACHE_TTL_MS) return null;
    return parsed; // { ts, data }
  } catch {
    return null; // private browsing / storage disabled — just skip the cache
  }
}

function writeCache(key, data) {
  try {
    localStorage.setItem(key, JSON.stringify({ ts: Date.now(), data }));
  } catch {
    // storage full or unavailable — cache is a convenience, fail silently
  }
}

function timeAgo(ts) {
  if (!ts) return "";
  const mins = Math.round((Date.now() - ts) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  const remMins = mins % 60;
  return `${hrs}h ${remMins}m ago`;
}

function formatDateShort(d) {
  try {
    return new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "short" });
  } catch {
    return d;
  }
}

// Finds the single highest day in a { date, amount } series — pure client
// computation on data already fetched, no extra query.
function findBestDay(series) {
  if (!series || series.length === 0) return null;
  let best = series[0];
  for (const d of series) {
    if (d.amount > best.amount) best = d;
  }
  if (best.amount <= 0) return null;
  return best;
}

// Finds the coupon code with the most redemptions (orders) — a different
// question than the list's default revenue sort. Pure client computation
// on data already fetched, no extra query.
function findMostRedeemedCoupon(discounts) {
  if (!discounts || discounts.length === 0) return null;
  let best = discounts[0];
  for (const d of discounts) {
    if (d.redemptions > best.redemptions) best = d;
  }
  if (best.redemptions <= 0) return null;
  return best;
}

// Splits a daily series in half and compares the two halves — a free trend
// signal computed from data we already fetched, not a true "vs last period"
// figure (that would need a second query). Returns null when there isn't
// enough data to say anything meaningful.
function computeTrend(series) {
  if (!series || series.length < 4) return null;
  const mid = Math.floor(series.length / 2);
  const sum = (arr) => arr.reduce((a, b) => a + b, 0);
  const firstSum = sum(series.slice(0, mid));
  const secondSum = sum(series.slice(mid));
  if (firstSum === 0 && secondSum === 0) return null;
  if (firstSum === 0) return { direction: "up", pct: 100 };
  const pct = ((secondSum - firstSum) / firstSum) * 100;
  return { direction: pct >= 0 ? "up" : "down", pct: Math.abs(Math.round(pct)) };
}

// Finds the single busiest hour in a { hour, count } series — pure client
// computation on data already fetched, no extra query.
function findPeakHour(hours) {
  if (!hours || hours.length === 0) return null;
  let best = hours[0];
  for (const h of hours) {
    if (h.count > best.count) best = h;
  }
  if (best.count <= 0) return null;
  return best;
}

// Buckets two aligned { date, count } series (chats and orders) by weekday
// and computes a conversion rate per weekday — pure client computation on
// data already fetched, no extra query.
function computeConversionByWeekday(chatsSeries, ordersSeries) {
  if (!chatsSeries || !ordersSeries || chatsSeries.length === 0) return [];
  const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const chatsByDate = {};
  chatsSeries.forEach((d) => { chatsByDate[d.date] = d.count; });
  const ordersByDate = {};
  ordersSeries.forEach((d) => { ordersByDate[d.date] = d.count; });

  const totals = WEEKDAYS.map(() => ({ chats: 0, orders: 0 }));
  const allDates = new Set([...Object.keys(chatsByDate), ...Object.keys(ordersByDate)]);
  allDates.forEach((date) => {
    const dow = new Date(date).getDay();
    totals[dow].chats += chatsByDate[date] || 0;
    totals[dow].orders += ordersByDate[date] || 0;
  });

  return WEEKDAYS.map((label, i) => ({
    weekday: label,
    rate: totals[i].chats > 0 ? Number(((totals[i].orders / totals[i].chats) * 100).toFixed(1)) : null,
  }));
}

// Computes average order value per day from aligned revenue/orders series —
// pure client computation on data already fetched, no extra query.
function computeDailyAOV(revenueSeries, ordersSeries) {
  if (!revenueSeries || !ordersSeries) return [];
  return revenueSeries.map((d, i) => {
    const orderCount = ordersSeries[i]?.count || 0;
    return { date: d.date, aov: orderCount > 0 ? d.amount / orderCount : 0 };
  });
}

// Finds the best and worst weekday in a { weekday, <valueKey> } series —
// pure client computation on data already fetched, no extra query.
function findBestWorstWeekday(series, valueKey) {
  if (!series || series.length === 0) return { best: null, worst: null };
  const valid = series.filter((d) => d[valueKey] !== null && d[valueKey] !== undefined);
  if (valid.length === 0) return { best: null, worst: null };
  let best = valid[0];
  let worst = valid[0];
  for (const d of valid) {
    if (d[valueKey] > best[valueKey]) best = d;
    if (d[valueKey] < worst[valueKey]) worst = d;
  }
  return { best, worst };
}

// Computes revenue-per-unit-discounted for each discount code, and flags
// the most efficient — pure client computation on data already fetched,
// no extra query.
function computeDiscountEfficiency(discounts) {
  if (!discounts || discounts.length === 0) return { rows: [], best: null };
  const rows = discounts.map((d) => ({
    ...d,
    efficiency: d.discount_given > 0 ? d.revenue / d.discount_given : null,
  }));
  const valid = rows.filter((d) => d.efficiency !== null);
  if (valid.length < 2) return { rows, best: null };
  let best = valid[0];
  for (const d of valid) {
    if (d.efficiency > best.efficiency) best = d;
  }
  return { rows, best };
}

// Computes drop-off % between consecutive funnel stages — pure client
// computation on data already fetched, no extra query.
function computeFunnelDropoff(funnel) {
  if (!funnel) return null;
  const { dms_received, orders_created, paid } = funnel;
  const stage1Drop =
    dms_received > 0 ? Number((((dms_received - orders_created) / dms_received) * 100).toFixed(1)) : null;
  const stage2Drop =
    orders_created > 0 ? Number((((orders_created - paid) / orders_created) * 100).toFixed(1)) : null;
  return { stage1Drop, stage2Drop };
}

// Finds the top market by revenue in a geography rows array — pure client
// computation on data already fetched, no extra query.
function findTopMarket(rows, nameKey) {
  if (!rows || rows.length === 0) return null;
  let best = rows[0];
  for (const r of rows) {
    if (r.revenue > best.revenue) best = r;
  }
  if (best.revenue <= 0) return null;
  return best;
}

function downloadTopProductsCSV(products, range) {
  const escape = (s) => `"${String(s ?? "").replace(/"/g, '""')}"`;
  const header = "Rank,Product,Units Sold,Revenue\n";
  const rows = products
    .map((p, i) => [i + 1, escape(p.product_name), p.units, p.revenue].join(","))
    .join("\n");
  const blob = new Blob([header + rows], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `checkoutdm-top-products-${range}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function triggerCSVDownload(filename, content) {
  const blob = new Blob([content], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// Exports every section already sitting in `data` as one CSV — pure
// client-side, nothing re-fetched, no DB cost.
function downloadFullReportCSV(data, range) {
  const esc = (s) => `"${String(s ?? "").replace(/"/g, '""')}"`;
  const lines = [];

  lines.push(`CheckoutDM Analytics Report — ${range}`, "");

  lines.push("KPIs");
  lines.push("Metric,Value");
  lines.push(`Revenue,${data.kpis.revenue}`);
  lines.push(`Paid Orders,${data.kpis.paid_orders}`);
  lines.push(`Chats,${data.kpis.chats}`);
  lines.push(`Conversion Rate %,${data.kpis.conversion_rate}`);
  lines.push(`Returns,${data.kpis.returns}`);
  lines.push(`Return Rate %,${data.kpis.return_rate}`);
  lines.push(`Discounts Given,${data.kpis.discounts_given}`);
  lines.push(`Orders With Discount,${data.kpis.discounted_orders_count}`);
  lines.push("");

  lines.push("Revenue by day");
  lines.push("Date,Amount");
  data.revenue_series.forEach((d) => lines.push(`${d.date},${d.amount}`));
  lines.push("");

  lines.push("Chats by day");
  lines.push("Date,Count");
  data.chats_series.forEach((d) => lines.push(`${d.date},${d.count}`));
  lines.push("");

  lines.push("Revenue by weekday");
  lines.push("Weekday,Amount");
  data.revenue_by_weekday.forEach((d) => lines.push(`${d.weekday},${d.amount}`));
  lines.push("");

  lines.push("Orders by day");
  lines.push("Date,Count");
  data.orders_series.forEach((d) => lines.push(`${d.date},${d.count}`));
  lines.push("");

  lines.push("Dead stock (lowest selling)");
  lines.push("Product,Units Sold");
  data.dead_stock.forEach((p) => lines.push(`${esc(p.product_name)},${p.units}`));
  lines.push("");

  lines.push(`Automation rate %,${data.automation_rate ?? ""}`);
  lines.push(`Auto-confirmed orders,${data.auto_confirmed_orders}`);
  lines.push(`Total confirmed orders,${data.total_confirmed_orders}`);
  lines.push(`Auto payment method connected,${data.has_auto_payment_method ? "Yes" : "No"}`);
  lines.push("");

  lines.push("Top products");
  lines.push("Rank,Product,Units Sold,Revenue");
  data.top_products.forEach((p, i) => lines.push(`${i + 1},${esc(p.product_name)},${p.units},${p.revenue}`));

  if (data.funnel) {
    lines.push("", "DM to Paid funnel");
    lines.push("Stage,Count");
    lines.push(`DMs received,${data.funnel.dms_received}`);
    lines.push(`Checkout started,${data.funnel.orders_created}`);
    lines.push(`Paid,${data.funnel.paid}`);
  }
  if (data.order_status_breakdown) {
    lines.push("", "Order status breakdown");
    lines.push("Status,Count");
    data.order_status_breakdown.forEach((s) => lines.push(`${esc(s.status)},${s.count}`));
  }
  if (data.return_reasons) {
    lines.push("", "Return reasons");
    lines.push("Reason,Count");
    data.return_reasons.forEach((r) => lines.push(`${esc(r.reason)},${r.count}`));
  }
  if (data.peak_hours) {
    lines.push("", "Chats by hour of day");
    lines.push("Hour,Count");
    data.peak_hours.forEach((h) => lines.push(`${h.hour}:00,${h.count}`));
  }
  if (typeof data.avg_checkout_minutes === "number") {
    lines.push("", `Avg. time DM to checkout (minutes),${data.avg_checkout_minutes}`);
  }
  if (typeof data.avg_return_resolution_hours === "number") {
    lines.push(`Avg. return resolution time (hours),${data.avg_return_resolution_hours}`);
  }
  if (typeof data.cart_abandonment_rate === "number") {
    lines.push(`Cart abandonment rate %,${data.cart_abandonment_rate}`);
  }
  if (typeof data.repeat_customer_rate === "number") {
    lines.push(`Repeat customer rate %,${data.repeat_customer_rate}`);
  }
  if (data.revenue_forecast) {
    lines.push("", "Revenue forecast (this calendar month)");
    lines.push(`Month to date revenue,${data.revenue_forecast.month_to_date_revenue}`);
    lines.push(`Projected month revenue,${data.revenue_forecast.projected_month_revenue}`);
  }
  if (data.customer_badges) {
    lines.push("", "Customer segments");
    lines.push("Badge,Count");
    Object.entries(data.customer_badges.counts).forEach(([k, v]) => lines.push(`${esc(k)},${v}`));

    if (data.customer_badges.top_spenders?.length) {
      lines.push("", "Top spenders");
      lines.push("Name,Phone,Total Spend,Orders");
      data.customer_badges.top_spenders.forEach((p) =>
        lines.push(`${esc(p.name)},${esc(p.phone)},${p.total_spend},${p.order_count}`)
      );
    }
  }

  if (data.discount_performance?.length) {
    lines.push("", "Discount code performance");
    lines.push("Code,Redemptions,Discount Given,Revenue");
    data.discount_performance.forEach((d) =>
      lines.push(`${esc(d.label)},${d.redemptions},${d.discount_given},${d.revenue}`)
    );
  }

  if (data.geography_countries?.length) {
    lines.push("", "Orders by country");
    lines.push("Country,Orders,Revenue");
    data.geography_countries.forEach((g) => lines.push(`${esc(g.country)},${g.orders},${g.revenue}`));
  }

  if (data.geography_states?.length) {
    lines.push("", "Orders by state");
    lines.push("State,Country,Orders,Revenue");
    data.geography_states.forEach((g) => lines.push(`${esc(g.state)},${esc(g.country)},${g.orders},${g.revenue}`));
  }

  if (data.geography_cities?.length) {
    lines.push("", "Orders by city");
    lines.push("City,State,Country,Orders,Revenue");
    data.geography_cities.forEach((g) =>
      lines.push(`${esc(g.city)},${esc(g.state)},${esc(g.country)},${g.orders},${g.revenue}`)
    );
  }

  triggerCSVDownload(`checkoutdm-analytics-report-${range}.csv`, lines.join("\n"));
}

/**
 * AnalyticsPanel
 * ------------------------------------------------------------------
 * Growth: revenue/chats over time, KPI row, top products.
 * Pro:    everything Growth has + DM -> Paid funnel, order status
 *         breakdown, and top return reasons.
 * Rendered from pages/Analytics.jsx (linked from the profile menu).
 * Free/Starter merchants get a clean locked state below — backend also
 * enforces this (403 upgrade_required), so a direct API call can't bypass it.
 * ------------------------------------------------------------------
 */

const RANGES = [
  { key: "7d", label: "7 days" },
  { key: "30d", label: "30 days" },
  { key: "90d", label: "90 days" },
];

export default function AnalyticsPanel({ merchant }) {
  const enabled = merchant?.plan === "growth" || merchant?.plan === "pro";
  const isPro = merchant?.plan === "pro";

  const [range, setRange] = useState("30d");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  // Cached per business + range for 2 hours in localStorage, so reloading
  // the page or coming back later doesn't hit the DB again until the cache
  // expires (or the merchant hits Refresh). Note: keyed on merchant.id — if
  // your /api/me response uses a different field for the business id,
  // swap it below so this doesn't fall back to a shared "me" key.
  const businessId = merchant?.id;

  const fetchAnalytics = (force) => {
    const key = cacheKey(businessId, range);
    if (!force) {
      const cached = readCache(key);
      if (cached) {
        setData(cached.data);
        setUpdatedAt(cached.ts);
        setError(false);
        setLoading(false);
        return;
      }
    }
    if (force) setRefreshing(true);
    else setLoading(true);
    setError(false);
    api
      .get(`/merchant/analytics?range=${range}`)
      .then((r) => {
        writeCache(key, r.data);
        setData(r.data);
        setUpdatedAt(Date.now());
      })
      .catch(() => setError(true))
      .finally(() => {
        setLoading(false);
        setRefreshing(false);
      });
  };

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return;
    }
    fetchAnalytics(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range, enabled]);

  if (!enabled) {
    return (
      <div className="rounded-[2rem] border border-ink/10 bg-white p-8 md:p-10" data-testid="analytics-locked">
        <div className="flex items-start gap-4">
          <span className="grid h-14 w-14 flex-none place-items-center rounded-3xl bg-ink/5 text-ink/60">
            <Lock size={22} />
          </span>
          <div className="flex-1">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-hotpink">Analytics</p>
            <h3 className="mt-1 font-display text-2xl font-black tracking-tight">
              Analytics starts on Growth
            </h3>
            <p className="mt-2 max-w-xl text-sm text-ink/60">
              Track revenue, chats, conversion and top products from Growth onward. Pro adds the
              DM → Paid funnel, order status breakdown and return reasons.
            </p>
            <a
              href="/plans"
              data-testid="analytics-upgrade-cta"
              className="cta-glow mt-5 inline-flex items-center gap-2 rounded-full bg-sunset-gradient px-5 py-2.5 text-sm font-semibold text-white"
            >
              Upgrade to Growth <ArrowUpRight size={14} />
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="analytics-panel">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-hotpink">
            {isPro ? "Pro" : "Growth"} analytics
          </p>
          <h3 className="mt-1 font-display text-2xl font-black tracking-tight">
            What your DMs are really doing.
          </h3>
        </div>
        <div className="flex flex-wrap items-center gap-3 print:hidden">
          {updatedAt && (
            <span className="inline-flex items-center gap-1 text-xs text-ink/40">
              <Clock size={12} /> Updated {timeAgo(updatedAt)}
            </span>
          )}
          <button
            onClick={() => fetchAnalytics(true)}
            disabled={refreshing || loading}
            data-testid="analytics-refresh"
            title="Cached for 2 hours — refresh to force a fresh pull"
            className="inline-flex items-center gap-1.5 rounded-full border border-ink/15 bg-white px-3 py-1.5 text-xs font-semibold text-ink/70 hover:bg-ink/5 disabled:opacity-50"
          >
            <RotateCw size={12} className={refreshing ? "animate-spin" : ""} /> Refresh
          </button>
          <div className="flex items-center rounded-full bg-white p-1 shadow-sm ring-1 ring-ink/10">
            {RANGES.map((r) => (
              <button
                key={r.key}
                onClick={() => setRange(r.key)}
                data-testid={`analytics-range-${r.key}`}
                className={`rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${
                  range === r.key ? "bg-ink text-cream" : "text-ink/60 hover:text-ink"
                }`}
              >
                {r.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {error ? (
        <div className="rounded-[2rem] border border-hotpink/30 bg-hotpink/5 p-8 text-center" data-testid="analytics-error">
          <p className="text-sm text-ink/70">Couldn't load analytics right now. Try again in a moment.</p>
        </div>
      ) : loading || !data ? (
        <div className="rounded-[2rem] border border-ink/10 bg-white p-10 text-center text-sm text-ink/50">
          Loading analytics…
        </div>
      ) : (
        <>
          {/* KPI row */}
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
            <Kpi
              label="Revenue"
              value={formatMoney(data.kpis.revenue, data.currency)}
              tone="bg-ink text-cream"
              icon={ShoppingBag}
              trend={computeTrend(data.revenue_series.map((d) => d.amount))}
              sparkline={data.revenue_series.map((d) => d.amount)}
              sparkColor="#FF4A74"
            />
            <Kpi
              label="Chats"
              value={data.kpis.chats.toLocaleString("en-IN")}
              tone="bg-mint"
              icon={MessageCircle}
              trend={computeTrend(data.chats_series.map((d) => d.count))}
              sparkline={data.chats_series.map((d) => d.count)}
              sparkColor="#161616"
              subtext="Active in this range"
            />
            <Kpi label="Paid orders" value={data.kpis.paid_orders.toLocaleString("en-IN")} tone="bg-peach" icon={Package} />
            <Kpi
              label="Avg. order value"
              value={formatMoney(
                data.kpis.paid_orders > 0 ? data.kpis.revenue / data.kpis.paid_orders : 0,
                data.currency
              )}
              tone="bg-white ring-1 ring-ink/10"
              icon={Wallet}
            />
            <Kpi
              label="Conversion"
              value={data.kpis.conversion_rate === null ? "—" : `${data.kpis.conversion_rate}%`}
              tone="bg-lilac"
              icon={Percent}
              subtext={data.kpis.conversion_rate === null ? "No active chats in range" : undefined}
            />
            <Kpi
              label="Returns filed"
              value={data.kpis.returns}
              tone="bg-white ring-1 ring-ink/10"
              icon={Undo2}
              subtext={`${data.kpis.return_rate}% of paid orders`}
            />
            <Kpi
              label="Discounts given"
              value={formatMoney(data.kpis.discounts_given, data.currency)}
              tone="bg-white ring-1 ring-ink/10"
              icon={Tag}
              subtext={
                data.kpis.discounted_orders_count > 0
                  ? `${
                      data.kpis.revenue > 0
                        ? `${((data.kpis.discounts_given / data.kpis.revenue) * 100).toFixed(1)}% of revenue · `
                        : ""
                    }${data.kpis.discounted_orders_count} order${data.kpis.discounted_orders_count === 1 ? "" : "s"} used a coupon`
                  : "No coupons used in range"
              }
            />
          </div>

          {/* Revenue + chats charts */}
          <div className="grid gap-4 lg:grid-cols-3">
            <div className="rounded-[2rem] border border-ink/10 bg-white p-6 lg:col-span-2">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-widest text-ink/50">Revenue over time</p>
                  <h2 className="mt-1 text-2xl font-black tracking-tight text-ink">
                    {formatMoney(data.kpis.revenue, data.currency)}
                  </h2>
                </div>
                {(() => {
                  const best = findBestDay(data.revenue_series);
                  return best ? (
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-hotpink/10 px-3 py-1.5 text-xs font-semibold text-hotpink">
                      <Star size={12} className="fill-hotpink" /> Best day: {formatDateShort(best.date)} ·{" "}
                      {formatMoney(best.amount, data.currency)}
                    </span>
                  ) : null;
                })()}
              </div>
              <div className="mt-6">
                <AreaChart
                  data={data.revenue_series.map((d) => d.amount)}
                  labels={data.revenue_series.map((d) => formatDateShort(d.date))}
                  formatValue={(v) => formatMoney(v, data.currency)}
                  highlightIndex={data.revenue_series.findIndex(
                    (d) => d.date === findBestDay(data.revenue_series)?.date
                  )}
                  height={220}
                />
              </div>
            </div>

            <div className="rounded-[2rem] bg-ink p-6 text-cream">
              <p className="text-xs font-semibold uppercase tracking-widest text-cream/60">DM engagement</p>
              <div className="mt-4 flex items-baseline gap-3">
                <BarChart3 className="h-7 w-7 text-hotpink" />
                <span className="text-4xl font-black tracking-tight">{data.kpis.chats}</span>
                <span className="text-sm text-cream/60">chats</span>
              </div>
              <div className="mt-6">
                <BarChart
                  data={data.chats_series.map((d) => d.count)}
                  labels={data.chats_series.map((d) => formatDateShort(d.date))}
                  formatValue={(v) => `${v} chat${v === 1 ? "" : "s"}`}
                />
              </div>
              <p className="mt-3 text-xs text-cream/60">Last {RANGES.find((r) => r.key === range)?.label}</p>
            </div>
          </div>

          {/* Top products */}
          <div className="rounded-[2rem] border border-ink/10 bg-white p-6">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h3 className="text-lg font-bold text-ink">Top products</h3>
              <div className="flex items-center gap-3">
                <span className="text-xs text-ink/50">By revenue, paid orders only</span>
                {data.top_products.length > 0 && (
                  <button
                    onClick={() => downloadTopProductsCSV(data.top_products, range)}
                    data-testid="analytics-export-top-products"
                    className="print:hidden inline-flex items-center gap-1.5 rounded-full border border-ink/15 bg-white px-3 py-1.5 text-xs font-semibold hover:bg-ink/5"
                  >
                    <Download size={12} /> Export CSV
                  </button>
                )}
              </div>
            </div>
            {data.top_products.length === 0 ? (
              <p className="mt-4 text-sm text-ink/50">No paid orders in this range yet.</p>
            ) : (
              <ul className="mt-4 divide-y divide-ink/5">
                {data.top_products.map((p, i) => (
                  <li key={p.product_name} className="flex items-center gap-4 py-3">
                    <span className="grid h-8 w-8 place-items-center rounded-full bg-ink/5 text-xs font-bold text-ink">
                      {i + 1}
                    </span>
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-ink">{p.product_name}</p>
                      <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-ink/5">
                        <div
                          className="h-full rounded-full bg-gradient-to-r from-hotpink via-red-500 to-orange-500"
                          style={{ width: `${(p.units / data.top_products[0].units) * 100}%` }}
                        />
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-bold text-ink">{formatMoney(p.revenue, data.currency)}</p>
                      <p className="text-xs text-ink/50">{p.units} sold</p>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Daily AOV trend */}
          {(() => {
            const dailyAOV = computeDailyAOV(data.revenue_series, data.orders_series);
            return (
              <div className="rounded-[2rem] border border-ink/10 bg-white p-6">
                <p className="text-xs font-semibold uppercase tracking-widest text-ink/50">Avg. order value over time</p>
                <h2 className="mt-1 text-2xl font-black tracking-tight text-ink">
                  {formatMoney(
                    data.kpis.paid_orders > 0 ? data.kpis.revenue / data.kpis.paid_orders : 0,
                    data.currency
                  )}
                </h2>
                <div className="mt-6">
                  <AreaChart
                    data={dailyAOV.map((d) => d.aov)}
                    labels={dailyAOV.map((d) => formatDateShort(d.date))}
                    formatValue={(v) => formatMoney(v, data.currency)}
                    height={180}
                  />
                </div>
              </div>
            );
          })()}

          {/* Discount code performance — Growth & Pro */}
          {data.discount_performance && data.discount_performance.length > 0 && (() => {
            const topByOrders = findMostRedeemedCoupon(data.discount_performance);
            const { rows: discountRows, best: mostEfficient } = computeDiscountEfficiency(data.discount_performance);
            return (
              <div className="rounded-[2rem] border border-ink/10 bg-white p-6">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <h3 className="text-lg font-bold text-ink">Discount code performance</h3>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-xs text-ink/50">Order revenue per code, this range</span>
                    {topByOrders && (
                      <span className="inline-flex items-center gap-1.5 rounded-full bg-hotpink/10 px-3 py-1.5 text-xs font-semibold text-hotpink">
                        <Tag size={12} /> Most orders: {topByOrders.label} · {topByOrders.redemptions} order{topByOrders.redemptions === 1 ? "" : "s"}
                      </span>
                    )}
                    {mostEfficient && (
                      <span className="inline-flex items-center gap-1.5 rounded-full bg-mint/30 px-3 py-1.5 text-xs font-semibold text-ink">
                        Most efficient: {mostEfficient.label} · {mostEfficient.efficiency.toFixed(1)}x return
                      </span>
                    )}
                  </div>
                </div>
                <ul className="mt-4 divide-y divide-ink/5">
                  {discountRows.map((d, i) => (
                    <li key={d.rule_id ?? `${d.label}-${i}`} className="flex items-center gap-4 py-3">
                      <span className="grid h-8 w-8 place-items-center rounded-full bg-ink/5 text-xs font-bold text-ink">
                        {i + 1}
                      </span>
                      <div className="flex-1">
                        <p className="flex items-center gap-1.5 text-sm font-semibold text-ink">
                          {d.label}
                          {topByOrders && (d.rule_id ?? d.label) === (topByOrders.rule_id ?? topByOrders.label) && (
                            <span className="inline-flex items-center rounded-full bg-hotpink/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-hotpink">
                              Most orders
                            </span>
                          )}
                        </p>
                        <p className="text-xs text-ink/50">
                          {d.redemptions} order{d.redemptions === 1 ? "" : "s"} · {formatMoney(d.discount_given, data.currency)} given away
                          {d.efficiency !== null && ` · ${d.efficiency.toFixed(1)}x return on discount`}
                        </p>
                      </div>
                      <p className="text-sm font-bold text-ink">{formatMoney(d.revenue, data.currency)}</p>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })()}

          {/* Revenue by weekday */}
          <div className="rounded-[2rem] border border-ink/10 bg-white p-6">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <h3 className="text-lg font-bold text-ink">Revenue by day of week</h3>
                <p className="text-xs text-ink/50">Which days actually convert, across this range</p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                {(() => {
                  const { best, worst } = findBestWorstWeekday(data.revenue_by_weekday, "amount");
                  return best && worst && best.weekday !== worst.weekday ? (
                    <>
                      <span className="inline-flex items-center gap-1.5 rounded-full bg-hotpink/10 px-3 py-1.5 text-xs font-semibold text-hotpink">
                        <Star size={12} className="fill-hotpink" /> Best: {best.weekday} · {formatMoney(best.amount, data.currency)}
                      </span>
                      <span className="inline-flex items-center gap-1.5 rounded-full bg-ink/5 px-3 py-1.5 text-xs font-semibold text-ink/60">
                        Slowest: {worst.weekday} · {formatMoney(worst.amount, data.currency)}
                      </span>
                    </>
                  ) : null;
                })()}
                <button
                  onClick={() => downloadFullReportCSV(data, range)}
                  data-testid="analytics-export-full-report"
                  className="print:hidden inline-flex items-center gap-1.5 rounded-full border border-ink/15 bg-white px-3 py-1.5 text-xs font-semibold hover:bg-ink/5"
                >
                  <Download size={12} /> Export full report
                </button>
              </div>
            </div>
            <div className="mt-6">
              <BarChart
                data={data.revenue_by_weekday.map((d) => d.amount)}
                labels={data.revenue_by_weekday.map((d) => d.weekday)}
                formatValue={(v) => formatMoney(v, data.currency)}
              />
              <div className="mt-2 flex justify-between text-[10px] font-semibold uppercase text-ink/30">
                {data.revenue_by_weekday.map((d) => (
                  <span key={d.weekday}>{d.weekday}</span>
                ))}
              </div>
            </div>
          </div>

          {/* Conversion by day of week */}
          {(() => {
            const conversionByWeekday = computeConversionByWeekday(data.chats_series, data.orders_series);
            const { best, worst } = findBestWorstWeekday(conversionByWeekday, "rate");
            return (
              <div className="rounded-[2rem] border border-ink/10 bg-white p-6">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <h3 className="text-lg font-bold text-ink">Conversion by day of week</h3>
                    <p className="text-xs text-ink/50">Paid orders as a share of chats, by weekday</p>
                  </div>
                  {best && worst && best.weekday !== worst.weekday && (
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="inline-flex items-center gap-1.5 rounded-full bg-hotpink/10 px-3 py-1.5 text-xs font-semibold text-hotpink">
                        <Star size={12} className="fill-hotpink" /> Best: {best.weekday} · {best.rate}%
                      </span>
                      <span className="inline-flex items-center gap-1.5 rounded-full bg-ink/5 px-3 py-1.5 text-xs font-semibold text-ink/60">
                        Weakest: {worst.weekday} · {worst.rate}%
                      </span>
                    </div>
                  )}
                </div>
                <div className="mt-6">
                  <BarChart
                    data={conversionByWeekday.map((d) => d.rate ?? 0)}
                    labels={conversionByWeekday.map((d) => d.weekday)}
                    formatValue={(v) => `${v}%`}
                  />
                </div>
                <div className="mt-2 flex justify-between text-[10px] font-semibold uppercase text-ink/30">
                  {conversionByWeekday.map((d) => (
                    <span key={d.weekday}>{d.weekday}</span>
                  ))}
                </div>
              </div>
            );
          })()}

          {/* Orders per day + Dead stock */}
          <div className="grid gap-4 lg:grid-cols-2">
            <div className="rounded-[2rem] border border-ink/10 bg-white p-6">
              <h3 className="text-lg font-bold text-ink">Orders per day</h3>
              <p className="text-xs text-ink/50">Paid orders, day by day</p>
              <div className="mt-6">
                <BarChart
                  data={data.orders_series.map((d) => d.count)}
                  labels={data.orders_series.map((d) => formatDateShort(d.date))}
                  formatValue={(v) => `${v} order${v === 1 ? "" : "s"}`}
                />
              </div>
            </div>

            <div className="rounded-[2rem] border border-ink/10 bg-white p-6">
              <h3 className="text-lg font-bold text-ink">Dead stock</h3>
              <p className="text-xs text-ink/50">Catalog items selling least in this range</p>
              {data.dead_stock.length === 0 ? (
                <p className="mt-4 text-sm text-ink/50">No catalog items yet.</p>
              ) : (
                <ul className="mt-4 space-y-2">
                  {data.dead_stock.map((p) => (
                    <li
                      key={p.product_name}
                      className="flex items-center justify-between rounded-xl bg-ink/[0.03] px-3 py-2"
                    >
                      <span className="text-sm font-semibold text-ink">{p.product_name}</span>
                      <span
                        className={`text-sm font-bold ${p.units === 0 ? "text-hotpink" : "text-ink"}`}
                      >
                        {p.units} sold
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          {/* Automation performance */}
          <AutomationSavings
            autoConfirmed={data.auto_confirmed_orders}
            totalConfirmed={data.total_confirmed_orders}
            automationRate={data.automation_rate}
            hasAutoPaymentMethod={data.has_auto_payment_method}
            currency={data.currency}
          />

          {/* Pro-only advanced section */}
          {isPro ? (
            <div>
              <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  <span className="inline-flex items-center gap-1 rounded-full bg-gradient-to-r from-hotpink via-red-500 to-orange-500 px-3 py-1 text-[11px] font-bold uppercase tracking-widest text-white">
                    <Sparkles className="h-3 w-3" /> Pro intelligence
                  </span>
                  <h2 className="text-xl font-bold text-ink">Advanced analytics</h2>
                </div>
                <button
                  onClick={() => window.print()}
                  data-testid="analytics-print-report"
                  className="print:hidden inline-flex items-center gap-1.5 rounded-full border border-ink/15 bg-white px-3 py-1.5 text-xs font-semibold hover:bg-ink/5"
                >
                  <Printer size={12} /> Print / share report
                </button>
              </div>

              {/* Revenue forecast */}
              <div className="mb-4 rounded-[2rem] bg-gradient-to-br from-mint/40 to-mint/10 p-6">
                <div className="flex items-center gap-2">
                  <TrendingUp className="h-4 w-4 text-ink/50" />
                  <h3 className="text-lg font-bold text-ink">Month-end revenue forecast</h3>
                </div>
                <p className="text-xs text-ink/50">
                  Simple run-rate projection — not a model, just today's pace held steady for the
                  rest of the month.
                </p>
                <div className="mt-4 flex flex-wrap items-end gap-x-8 gap-y-2">
                  <div>
                    <p className="text-3xl font-black tracking-tight text-ink">
                      {formatMoney(data.revenue_forecast.projected_month_revenue, data.currency)}
                    </p>
                    <p className="text-xs text-ink/50">Projected by month end</p>
                  </div>
                  <div>
                    <p className="text-xl font-bold text-ink">
                      {formatMoney(data.revenue_forecast.month_to_date_revenue, data.currency)}
                    </p>
                    <p className="text-xs text-ink/50">
                      So far · day {data.revenue_forecast.days_elapsed} of {data.revenue_forecast.days_in_month}
                    </p>
                  </div>
                </div>
              </div>

              <div className="grid gap-4 lg:grid-cols-2">
                {/* Funnel */}
                <div className="rounded-[2rem] border border-ink/10 bg-white p-6">
                  <h3 className="text-lg font-bold text-ink">DM → Paid funnel</h3>
                  <p className="text-xs text-ink/50">
                    DMs started, orders created (checkout link sent), and orders paid.
                  </p>
                  <div className="mt-6 space-y-3">
                    {(() => {
                      const dropoff = computeFunnelDropoff(data.funnel);
                      const stages = [
                        { stage: "DMs received", count: data.funnel.dms_received },
                        { stage: "Checkout started", count: data.funnel.orders_created, dropPct: dropoff?.stage1Drop },
                        { stage: "Paid", count: data.funnel.paid, dropPct: dropoff?.stage2Drop },
                      ];
                      const base = stages[0].count || 1;
                      return stages.map((f, i) => {
                        const w = (f.count / base) * 100;
                        return (
                          <div key={f.stage}>
                            {i > 0 && f.dropPct !== null && f.dropPct !== undefined && (
                              <p className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-hotpink/70">
                                ↓ {f.dropPct}% drop-off
                              </p>
                            )}
                            <div className="flex items-baseline justify-between text-sm">
                              <span className="font-semibold text-ink">{f.stage}</span>
                              <span className="text-ink/60">{f.count.toLocaleString("en-IN")}</span>
                            </div>
                            <div className="mt-1 h-3 overflow-hidden rounded-full bg-ink/5">
                              <motion.div
                                initial={{ width: 0 }}
                                animate={{ width: `${w}%` }}
                                transition={{ delay: i * 0.1, duration: 0.6 }}
                                className={`h-full rounded-full ${
                                  i === 2 ? "bg-gradient-to-r from-hotpink via-red-500 to-orange-500" : "bg-ink"
                                }`}
                              />
                            </div>
                          </div>
                        );
                      });
                    })()}
                  </div>
                </div>

                {/* Order status breakdown */}
                <div className="rounded-[2rem] border border-ink/10 bg-white p-6">
                  <div className="flex items-center gap-2">
                    <Filter className="h-4 w-4 text-ink/40" />
                    <h3 className="text-lg font-bold text-ink">Order status breakdown</h3>
                  </div>
                  {data.order_status_breakdown.length === 0 ? (
                    <p className="mt-4 text-sm text-ink/50">No orders in this range yet.</p>
                  ) : (
                    <ul className="mt-4 space-y-2">
                      {data.order_status_breakdown.map((s) => (
                        <li key={s.status} className="flex items-center justify-between rounded-xl bg-ink/[0.03] px-3 py-2">
                          <span className="text-sm font-semibold capitalize text-ink">{s.status.replace(/_/g, " ")}</span>
                          <span className="text-sm font-bold text-ink">{s.count}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>

              {/* Return reasons */}
              <div className="mt-4 rounded-[2rem] border border-ink/10 bg-white p-6">
                <div className="flex items-center gap-2">
                  <RotateCcw className="h-4 w-4 text-ink/40" />
                  <h3 className="text-lg font-bold text-ink">Top return reasons</h3>
                </div>
                {data.return_reasons.length === 0 ? (
                  <p className="mt-4 text-sm text-ink/50">No returns in this range. Nice.</p>
                ) : (
                  <ul className="mt-4 space-y-2">
                    {data.return_reasons.map((r) => (
                      <li key={r.reason} className="flex items-center justify-between rounded-xl bg-ink/[0.03] px-3 py-2">
                        <span className="text-sm font-semibold text-ink">{r.reason}</span>
                        <span className="text-sm font-bold text-ink">{r.count}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {/* Returns by day of week */}
              {data.returns_by_weekday && data.returns_by_weekday.some((d) => d.count > 0) && (
                <div className="mt-4 rounded-[2rem] border border-ink/10 bg-white p-6">
                  <div className="flex items-center gap-2">
                    <RotateCcw className="h-4 w-4 text-ink/40" />
                    <h3 className="text-lg font-bold text-ink">Returns by day of week</h3>
                  </div>
                  <p className="text-xs text-ink/50">When return requests get filed, this range</p>
                  <div className="mt-6">
                    <BarChart
                      data={data.returns_by_weekday.map((d) => d.count)}
                      labels={data.returns_by_weekday.map((d) => d.weekday)}
                      formatValue={(v) => `${v} return${v === 1 ? "" : "s"}`}
                    />
                  </div>
                  <div className="mt-2 flex justify-between text-[10px] font-semibold uppercase text-ink/30">
                    {data.returns_by_weekday.map((d) => (
                      <span key={d.weekday}>{d.weekday}</span>
                    ))}
                  </div>
                </div>
              )}

              {/* Operational metrics */}
              <div className="mt-4 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
                <ProStat
                  icon={Timer}
                  label="Avg. DM → checkout"
                  value={
                    data.avg_checkout_minutes === null
                      ? "—"
                      : data.avg_checkout_minutes < 60
                      ? `${data.avg_checkout_minutes}m`
                      : `${(data.avg_checkout_minutes / 60).toFixed(1)}h`
                  }
                  hint="Time from first DM to checkout link created"
                />
                <ProStat
                  icon={Undo2}
                  label="Avg. return resolution"
                  value={data.avg_return_resolution_hours === null ? "—" : `${data.avg_return_resolution_hours}h`}
                  hint="Time from return request to decision"
                />
                <ProStat
                  icon={ShoppingCart}
                  label="Cart abandonment"
                  value={`${data.cart_abandonment_rate}%`}
                  hint="Carts with items, never checked out"
                />
                <ProStat
                  icon={Repeat}
                  label="Repeat customers"
                  value={`${data.repeat_customer_rate}%`}
                  hint={`${data.repeat_customers_count} of ${data.total_paying_customers} buyers · updated daily`}
                />
              </div>

              {/* Peak DM hours */}
              <div className="mt-4 rounded-[2rem] border border-ink/10 bg-white p-6">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div>
                    <div className="flex items-center gap-2">
                      <Clock className="h-4 w-4 text-ink/40" />
                      <h3 className="text-lg font-bold text-ink">When DMs come in</h3>
                    </div>
                    <p className="text-xs text-ink/50">Chats started, by hour of day (24h)</p>
                  </div>
                  {(() => {
                    const peak = findPeakHour(data.peak_hours);
                    return peak ? (
                      <span className="inline-flex items-center gap-1.5 rounded-full bg-hotpink/10 px-3 py-1.5 text-xs font-semibold text-hotpink">
                        <Star size={12} className="fill-hotpink" /> Busiest: {peak.hour}:00 · {peak.count} chat{peak.count === 1 ? "" : "s"}
                      </span>
                    ) : null;
                  })()}
                </div>
                <div className="mt-6">
                  <BarChart
                    data={data.peak_hours.map((h) => h.count)}
                    labels={data.peak_hours.map((h) => `${h.hour}:00`)}
                    formatValue={(v) => `${v} chat${v === 1 ? "" : "s"}`}
                  />
                </div>
              </div>

              {/* Customer badges */}
              <div className="mt-4 rounded-[2rem] border border-ink/10 bg-white p-6">
                <div className="flex items-center gap-2">
                  <Users className="h-4 w-4 text-ink/40" />
                  <h3 className="text-lg font-bold text-ink">Customer segments</h3>
                </div>
                <p className="text-xs text-ink/50">
                  All-time, across every paying customer — updated daily, same cache as repeat rate.
                  VIP = 3+ orders and above-average spend. At Risk = quiet 60+ days. Lost = quiet
                  120+ days. A customer only gets one badge — Lost and At Risk take priority over
                  VIP, since a valuable customer going quiet is the thing most worth acting on.
                </p>

                <div className="mt-5 grid grid-cols-3 gap-3 sm:grid-cols-6">
                  {[
                    { key: "vip", label: "VIP", color: "bg-lilac" },
                    { key: "loyal", label: "Loyal", color: "bg-mint" },
                    { key: "at_risk", label: "At Risk", color: "bg-peach" },
                    { key: "new", label: "New", color: "bg-white ring-1 ring-ink/10" },
                    { key: "lost", label: "Lost", color: "bg-hotpink/10" },
                    { key: "regular", label: "Regular", color: "bg-white ring-1 ring-ink/10" },
                  ].map((b) => (
                    <div key={b.key} className={`rounded-2xl p-3 text-center ${b.color}`}>
                      <p className="text-xl font-black text-ink">{data.customer_badges.counts[b.key] || 0}</p>
                      <p className="text-[10px] font-semibold uppercase tracking-wide text-ink/50">{b.label}</p>
                    </div>
                  ))}
                </div>

                <div className="mt-6 grid gap-4 lg:grid-cols-2 xl:grid-cols-4">
                  <CustomerList
                    title="Top spenders"
                    icon={Crown}
                    people={data.customer_badges.top_spenders}
                    emptyText="No paying customers yet"
                    metric={(p) => `${formatMoney(p.total_spend, data.currency)} · ${p.order_count} order${p.order_count === 1 ? "" : "s"}`}
                  />
                  <CustomerList
                    title="Top VIPs"
                    icon={Star}
                    people={data.customer_badges.vip_list}
                    emptyText="No VIP customers yet"
                    metric={(p) => formatMoney(p.total_spend, data.currency)}
                  />
                  <CustomerList
                    title="At risk — worth a nudge"
                    icon={AlertTriangle}
                    people={data.customer_badges.at_risk_list}
                    emptyText="No at-risk customers"
                    metric={(p) => `${formatMoney(p.total_spend, data.currency)} · quiet since ${formatDateShort(p.last_order_date)}`}
                  />
                  <CustomerList
                    title="Lost — win-back targets"
                    icon={UserX}
                    people={data.customer_badges.lost_list}
                    emptyText="No lost customers"
                    metric={(p) => `${formatMoney(p.total_spend, data.currency)} · quiet since ${formatDateShort(p.last_order_date)}`}
                  />
                </div>
              </div>

              {/* Geography — Pro only */}
              {(data.geography_countries?.length > 0 ||
                data.geography_states?.length > 0 ||
                data.geography_cities?.length > 0) && (
                <GeographyPanel data={data} />
              )}
            </div>
          ) : (
            <ProTeaser />
          )}
        </>
      )}
    </div>
  );
}

// -------------------- Geography panel (tabbed pie + list) -------------------
const GEO_TABS = [
  { key: "country", label: "Country", nameKey: "country", dataKey: "geography_countries" },
  { key: "state", label: "State", nameKey: "state", dataKey: "geography_states" },
  { key: "city", label: "City", nameKey: "city", dataKey: "geography_cities" },
];

function GeographyPanel({ data }) {
  const availableTabs = GEO_TABS.filter((t) => data[t.dataKey]?.length > 0);
  const [activeTab, setActiveTab] = useState(availableTabs[0]?.key || "country");
  const tab = availableTabs.find((t) => t.key === activeTab) || availableTabs[0];
  const rows = tab ? data[tab.dataKey] : [];

  if (!tab) return null;

  const subLabel = (row) => {
    if (tab.key === "city") return [row.state, row.country].filter(Boolean).join(", ");
    if (tab.key === "state") return row.country;
    return null;
  };

  return (
    <div className="mt-4 rounded-[2rem] border border-ink/10 bg-white p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <MapPin className="h-4 w-4 text-ink/40" />
            <h3 className="text-lg font-bold text-ink">Where orders come from</h3>
          </div>
          <p className="text-xs text-ink/50">By delivery address, this range</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {(() => {
            const topMarket = findTopMarket(rows, tab.nameKey);
            return topMarket ? (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-hotpink/10 px-3 py-1.5 text-xs font-semibold text-hotpink">
                <Star size={12} className="fill-hotpink" /> Top: {topMarket[tab.nameKey]} · {formatMoney(topMarket.revenue, data.currency)}
              </span>
            ) : null;
          })()}
          <div className="flex items-center rounded-full bg-ink/5 p-1">
            {availableTabs.map((t) => (
              <button
                key={t.key}
                onClick={() => setActiveTab(t.key)}
                className={`rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${
                  tab.key === t.key ? "bg-ink text-cream" : "text-ink/60 hover:text-ink"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-2">
        <div>
          <ResponsiveContainer width="100%" height={260}>
            <PieChart>
              <Pie
                data={rows}
                dataKey="revenue"
                nameKey={tab.nameKey}
                innerRadius={55}
                outerRadius={95}
                paddingAngle={2}
              >
                {rows.map((_, i) => (
                  <Cell key={i} fill={GEO_COLORS[i % GEO_COLORS.length]} />
                ))}
              </Pie>
              <Tooltip formatter={(v) => formatMoney(v, data.currency)} />
              <Legend layout="vertical" align="right" verticalAlign="middle" wrapperStyle={{ fontSize: 12 }} />
            </PieChart>
          </ResponsiveContainer>
        </div>

        <div>
          <ul className="space-y-2">
            {rows.map((row, i) => (
              <li
                key={`${row[tab.nameKey]}-${i}`}
                className="flex items-center justify-between gap-3 rounded-xl bg-ink/[0.03] px-3 py-2"
              >
                <div>
                  <span className="text-sm font-semibold text-ink">{row[tab.nameKey]}</span>
                  {subLabel(row) && <span className="ml-1 text-xs text-ink/40">{subLabel(row)}</span>}
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-xs text-ink/60">
                    {row.orders} order{row.orders === 1 ? "" : "s"}
                  </span>
                  <span className="text-sm font-bold text-ink">{formatMoney(row.revenue, data.currency)}</span>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

// -------------------- Automation performance calculator ---------------------
// Real, measured number in: auto-resolved conversation count. Everything
// past that (hours/cost saved) is a transparent calculator driven by
// assumptions the merchant sets and we persist locally -- not a measurement,
// and labeled as such, since we have no message-level log to measure it for real.
function AutomationSavings({ autoConfirmed, totalConfirmed, automationRate, hasAutoPaymentMethod, currency }) {
  const [minutesPerReply, setMinutesPerReply] = useState(() => {
    try {
      return Number(localStorage.getItem("cdm_automation_minutes")) || 4;
    } catch {
      return 4;
    }
  });
  const [hourlyRate, setHourlyRate] = useState(() => {
    try {
      return Number(localStorage.getItem("cdm_automation_hourly_rate")) || 150;
    } catch {
      return 150;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem("cdm_automation_minutes", String(minutesPerReply));
    } catch {
      // ignore — persistence is a convenience only
    }
  }, [minutesPerReply]);

  useEffect(() => {
    try {
      localStorage.setItem("cdm_automation_hourly_rate", String(hourlyRate));
    } catch {
      // ignore — persistence is a convenience only
    }
  }, [hourlyRate]);

  const hoursSaved = ((autoConfirmed || 0) * minutesPerReply) / 60;
  const costSaved = hoursSaved * hourlyRate;

  // No Razorpay/Stripe connected means every order is necessarily manual --
  // 0% is technically correct but reads as "broken." Reframe it as a
  // projection of what automating would save, using their own manual
  // order volume as the baseline, instead of a flat zero.
  const potentialHoursSaved = ((totalConfirmed || 0) * minutesPerReply) / 60;
  const potentialCostSaved = potentialHoursSaved * hourlyRate;

  return (
    <div className="rounded-[2rem] border border-ink/10 bg-white p-6">
      <h3 className="text-lg font-bold text-ink">Automation performance</h3>
      <p className="text-xs text-ink/50">
        {hasAutoPaymentMethod
          ? "Paid orders confirmed automatically, without needing manual payment review, in this range."
          : "You're on manual payment verification only, so every order needs your review right now."}
      </p>

      {!hasAutoPaymentMethod && totalConfirmed > 0 && (
        <div className="mt-4 rounded-2xl bg-hotpink/5 p-4 text-xs text-ink/70">
          <p className="font-semibold text-ink">
            Connect Razorpay or Stripe to start auto-confirming payments.
          </p>
          <p className="mt-1">
            Based on your {totalConfirmed} manually-reviewed order{totalConfirmed === 1 ? "" : "s"} in this
            range, that's roughly <strong className="text-ink">{potentialHoursSaved.toFixed(1)}h</strong> and{" "}
            <strong className="text-ink">{formatMoney(potentialCostSaved, currency)}</strong> of manual review
            you could avoid.
          </p>
        </div>
      )}

      <div className="mt-6 grid gap-4 sm:grid-cols-4">
        <div>
          <p className="text-3xl font-black text-ink">
            {automationRate === null ? "—" : `${automationRate}%`}
          </p>
          <p className="text-xs text-ink/50">
            {totalConfirmed > 0
              ? `${autoConfirmed} of ${totalConfirmed} orders`
              : "No confirmed orders yet"}
          </p>
        </div>
        <div>
          <p className="text-3xl font-black text-ink">{autoConfirmed || 0}</p>
          <p className="text-xs text-ink/50">Auto-confirmed orders</p>
        </div>
        <div>
          <p className="text-3xl font-black text-ink">
            {hasAutoPaymentMethod ? `${hoursSaved.toFixed(1)}h` : `${potentialHoursSaved.toFixed(1)}h`}
          </p>
          <p className="text-xs text-ink/50">
            {hasAutoPaymentMethod ? "Estimated hours saved" : "Hours you could save"}
          </p>
        </div>
        <div>
          <p className="text-3xl font-black text-ink">
            {formatMoney(hasAutoPaymentMethod ? costSaved : potentialCostSaved, currency)}
          </p>
          <p className="text-xs text-ink/50">
            {hasAutoPaymentMethod ? "Estimated staff cost saved" : "Staff cost you could save"}
          </p>
        </div>
      </div>
      <div className="mt-5 flex flex-wrap items-center gap-4 rounded-2xl bg-ink/[0.03] p-4 text-xs print:hidden">
        <span className="font-semibold text-ink/60">Your assumptions:</span>
        <label className="flex items-center gap-1.5">
          <input
            type="number"
            min="0"
            step="0.5"
            value={minutesPerReply}
            onChange={(e) => setMinutesPerReply(Math.max(0, Number(e.target.value) || 0))}
            className="w-16 rounded-lg border border-ink/15 bg-white px-2 py-1 text-xs"
          />
          min per manual reply
        </label>
        <label className="flex items-center gap-1.5">
          <input
            type="number"
            min="0"
            step="10"
            value={hourlyRate}
            onChange={(e) => setHourlyRate(Math.max(0, Number(e.target.value) || 0))}
            className="w-20 rounded-lg border border-ink/15 bg-white px-2 py-1 text-xs"
          />
          {CURRENCY_SYMBOLS[currency] || currency}/hour staff cost
        </label>
      </div>
      <p className="mt-2 text-[10px] text-ink/30">
        Estimate based on the assumptions above, not a measurement — adjust them to match your
        actual team.
      </p>
    </div>
  );
}

// -------------------- KPI card ---------------------------------------------
function Kpi({ label, value, tone, icon: Icon, trend, sparkline, sparkColor = "currentColor", subtext }) {
  const Trend = trend?.direction === "down" ? TrendingDown : TrendingUp;
  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className={`rounded-[2rem] p-5 ${tone}`}>
      <div className="flex items-center justify-between">
        <p className={`text-[11px] font-bold uppercase tracking-widest ${tone.includes("bg-ink") ? "text-cream/60" : "text-ink/60"}`}>
          {label}
        </p>
        <Icon className="h-4 w-4 opacity-60" />
      </div>
      <p className={`mt-6 text-3xl font-black tracking-tight ${tone.includes("bg-ink") ? "text-cream" : "text-ink"}`}>
        {value}
      </p>
      {sparkline && sparkline.length >= 2 && <Sparkline data={sparkline} color={sparkColor} />}
      {trend && (
        <p
          className={`mt-1 inline-flex items-center gap-1 text-xs font-semibold ${
            trend.direction === "up" ? "text-mint" : "text-hotpink"
          }`}
          title="Second half of this range vs the first half — not a true previous-period comparison"
        >
          <Trend className="h-3 w-3" /> {trend.pct}% within range
        </p>
      )}
      {subtext && <p className="mt-1 text-xs text-ink/40">{subtext}</p>}
    </motion.div>
  );
}

// -------------------- Sparkline --------------------------------------------
function Sparkline({ data, color }) {
  const w = 100;
  const h = 28;
  const pad = 2;
  const max = Math.max(...data);
  const min = Math.min(...data);
  const stepX = (w - pad * 2) / (data.length - 1 || 1);
  const points = data
    .map((v, i) => {
      const x = pad + i * stepX;
      const y = pad + ((max - v) / (max - min || 1)) * (h - pad * 2);
      return `${x},${y}`;
    })
    .join(" ");
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="mt-3 h-7 w-full" preserveAspectRatio="none">
      <polyline points={points} fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" opacity="0.85" />
    </svg>
  );
}

// -------------------- Area chart --------------------------------------------
function AreaChart({ data, labels, formatValue, height = 200, highlightIndex = -1 }) {
  const [hover, setHover] = useState(null);
  const w = 800;
  const h = height;
  const pad = 8;
  const series = data.length ? data : [0, 0];
  const max = Math.max(...series);
  const min = Math.min(...series);
  const stepX = (w - pad * 2) / (series.length - 1 || 1);
  const points = series.map((v, i) => {
    const x = pad + i * stepX;
    const y = pad + ((max - v) / (max - min || 1)) * (h - pad * 2);
    return [x, y];
  });
  const linePath = points.map(([x, y], i) => `${i === 0 ? "M" : "L"}${x},${y}`).join(" ");
  const areaPath = `${linePath} L${points[points.length - 1][0]},${h} L${points[0][0]},${h} Z`;

  const handleMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const relX = ((e.clientX - rect.left) / rect.width) * w;
    let idx = Math.round((relX - pad) / stepX);
    idx = Math.max(0, Math.min(series.length - 1, idx));
    setHover(idx);
  };

  return (
    <div className="relative" onMouseLeave={() => setHover(null)}>
      <svg
        viewBox={`0 0 ${w} ${h}`}
        className="w-full cursor-crosshair"
        preserveAspectRatio="none"
        onMouseMove={handleMove}
      >
        <defs>
          <linearGradient id="analyticsAreaFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#FF4A74" stopOpacity="0.35" />
            <stop offset="100%" stopColor="#FF4A74" stopOpacity="0" />
          </linearGradient>
        </defs>
        <path d={areaPath} fill="url(#analyticsAreaFill)" />
        <motion.path
          d={linePath}
          fill="none"
          stroke="#FF4A74"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          initial={{ pathLength: 0 }}
          animate={{ pathLength: 1 }}
          transition={{ duration: 1.2, ease: "easeInOut" }}
        />
        {hover !== null && (
          <line x1={points[hover][0]} y1={0} x2={points[hover][0]} y2={h} stroke="#161616" strokeOpacity="0.12" strokeWidth="1.5" />
        )}
        {points.map(([x, y], i) => (
          <circle key={i} cx={x} cy={y} r={i === hover ? 5 : i === points.length - 1 ? 4 : 0} fill="#FF4A74" />
        ))}
        {highlightIndex >= 0 && points[highlightIndex] && hover !== highlightIndex && (
          <circle
            cx={points[highlightIndex][0]}
            cy={points[highlightIndex][1]}
            r="4.5"
            fill="#FFF"
            stroke="#FF4A74"
            strokeWidth="2.5"
          />
        )}
      </svg>
      {hover !== null && (
        <div
          className="pointer-events-none absolute z-10 -translate-x-1/2 -translate-y-[calc(100%+10px)] whitespace-nowrap rounded-lg bg-ink px-2.5 py-1.5 text-xs font-semibold text-cream shadow-lg"
          style={{ left: `${(points[hover][0] / w) * 100}%`, top: `${(points[hover][1] / h) * 100}%` }}
        >
          {labels?.[hover] && <div className="text-[10px] font-normal text-cream/60">{labels[hover]}</div>}
          {formatValue ? formatValue(series[hover]) : series[hover]}
        </div>
      )}
    </div>
  );
}

// -------------------- Bar chart ---------------------------------------------
function BarChart({ data, labels, formatValue }) {
  const [hover, setHover] = useState(null);
  const series = data.length ? data : [0];
  const max = Math.max(...series, 1);
  return (
    <div className="flex h-24 items-end gap-1.5">
      {series.map((v, i) => (
        <div
          key={i}
          className="relative flex h-full flex-1 items-end"
          onMouseEnter={() => setHover(i)}
          onMouseLeave={() => setHover((h) => (h === i ? null : h))}
        >
          {hover === i && (
            <div className="pointer-events-none absolute -top-9 left-1/2 z-10 -translate-x-1/2 whitespace-nowrap rounded-lg bg-cream px-2 py-1 text-[10px] font-semibold text-ink shadow-lg ring-1 ring-ink/10">
              {labels?.[i] && <span className="mr-1 font-normal text-ink/50">{labels[i]}</span>}
              {formatValue ? formatValue(v) : v}
            </div>
          )}
          <motion.div
            initial={{ height: 0 }}
            animate={{ height: `${(v / max) * 100}%` }}
            transition={{ delay: i * 0.02, duration: 0.5 }}
            className={`w-full rounded-t-md bg-hotpink transition-all ${hover === i ? "ring-2 ring-white/80" : ""}`}
            style={{ minHeight: 3 }}
          />
        </div>
      ))}
    </div>
  );
}

// -------------------- Customer list (VIP / At Risk / Lost) ------------------
function CustomerList({ title, icon: Icon, people, emptyText, metric }) {
  return (
    <div className="rounded-2xl border border-ink/10 bg-ink/[0.02] p-4">
      <div className="flex items-center gap-1.5">
        <Icon className="h-3.5 w-3.5 text-hotpink" />
        <p className="text-xs font-bold uppercase tracking-wide text-ink/60">{title}</p>
      </div>
      {people.length === 0 ? (
        <p className="mt-3 text-xs text-ink/40">{emptyText}</p>
      ) : (
        <ul className="mt-3 space-y-2">
          {people.map((p) => (
            <li key={p.phone} className="text-xs">
              <p className="font-semibold text-ink">{p.name || p.phone || "Unknown"}</p>
              <p className="text-ink/50">{metric(p)}</p>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// -------------------- Pro operational stat card -----------------------------
function ProStat({ icon: Icon, label, value, hint }) {
  return (
    <div className="rounded-[2rem] border border-ink/10 bg-white p-5">
      <div className="flex items-center gap-2">
        <span className="grid h-8 w-8 flex-none place-items-center rounded-xl bg-hotpink/10 text-hotpink">
          <Icon size={15} />
        </span>
        <p className="text-xs font-semibold uppercase tracking-widest text-ink/50">{label}</p>
      </div>
      <p className="mt-3 text-2xl font-black tracking-tight text-ink">{value}</p>
      <p className="mt-1 text-xs text-ink/40">{hint}</p>
    </div>
  );
}

// -------------------- Pro teaser -------------------------------------------
function ProTeaser() {
  return (
    <div className="overflow-hidden rounded-[2rem] bg-gradient-to-br from-ink to-ink/90 p-8 text-cream md:p-10">
      <div className="grid gap-8 md:grid-cols-2 md:items-center">
        <div>
          <span className="inline-flex items-center gap-1 rounded-full bg-hotpink px-3 py-1 text-[11px] font-bold uppercase tracking-widest text-white">
            <Sparkles className="h-3 w-3" /> Pro only
          </span>
          <h3 className="mt-4 text-2xl font-black tracking-tight md:text-3xl">
            The operational view — unlocked on Pro.
          </h3>
          <p className="mt-3 text-cream/70">
            See where DMs stop converting, how fast checkout happens, who your repeat buyers are,
            and when to be online — not just what happened.
          </p>
          <a
            href="/plans"
            data-testid="analytics-upgrade-pro"
            className="cta-glow mt-6 inline-flex items-center gap-1.5 rounded-full bg-gradient-to-r from-hotpink via-red-500 to-orange-500 px-5 py-2.5 text-sm font-semibold text-white shadow-sm"
          >
            Upgrade to Pro <ArrowUpRight className="h-4 w-4" />
          </a>
        </div>
        <ul className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
          {[
            { label: "DM → Paid funnel", icon: BarChart3 },
            { label: "Order status breakdown", icon: Filter },
            { label: "Top return reasons", icon: RotateCcw },
            { label: "Avg. DM → checkout time", icon: Timer },
            { label: "Repeat customer rate", icon: Repeat },
            { label: "Cart abandonment rate", icon: ShoppingCart },
            { label: "Peak DM hours", icon: Clock },
            { label: "Revenue forecast", icon: TrendingUp },
            { label: "VIP / At Risk / Lost badges", icon: Users },
            { label: "Printable report", icon: Printer },
          ].map((f) => (
            <li key={f.label} className="flex items-center gap-2.5 rounded-2xl border border-cream/10 bg-cream/[0.04] p-3.5">
              <f.icon className="h-4 w-4 flex-none text-hotpink" />
              <p className="text-xs font-semibold leading-tight">{f.label}</p>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
